import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export function useSocialAgent() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);

  // Connection state
  const [status, setStatus] = useState(null);
  const [posts, setPosts] = useState([]);
  const [schedule, setSchedule] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, statusRes, postsRes, scheduleRes] = await Promise.all([
        authAxiosRef.current.get("/social-agent/subscription/status"),
        authAxiosRef.current.get("/social-agent/subscription/plans"),
        authAxiosRef.current.get("/social-agent/status"),
        authAxiosRef.current.get("/social-agent/posts?limit=10"),
        authAxiosRef.current.get("/social-agent/subscription/schedule")
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data.plans || []);
      setStatus(statusRes.data);
      setPosts(postsRes.data.posts || []);
      setSchedule(scheduleRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();

    if (searchParams.get("subscription") === "success") {
      toast.success("Assinatura ativada com sucesso!");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchData, searchParams]);

  const handleConnect = async (accessToken, onSuccess) => {
    if (!accessToken.trim()) {
      toast.error("Por favor, insira o Access Token");
      return;
    }
    setSaving(true);
    try {
      const res = await authAxios.post("/social-agent/connect", { access_token: accessToken.trim() });
      toast.success(res.data.message || "Conta conectada!");
      onSuccess?.();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao conectar");
    }
    setSaving(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Deseja desconectar a conta Meta?")) return;
    setSaving(true);
    try {
      await authAxios.delete("/social-agent/disconnect");
      toast.success("Conta desconectada");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao desconectar");
    }
    setSaving(false);
  };

  const handleSubscribe = async (planId) => {
    setSaving(true);
    try {
      const res = await authAxios.post("/social-agent/subscription/subscribe", { plan: planId });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao processar");
    }
    setSaving(false);
  };

  const handleActivateTrial = async (onSuccess) => {
    setSaving(true);
    try {
      const res = await authAxios.post("/social-agent/subscription/activate-trial");
      toast.success(res.data.message);
      fetchData();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao ativar trial");
    }
    setSaving(false);
  };

  const handleCreatePost = async (newPost, onSuccess) => {
    if (!newPost.content.trim()) {
      toast.error("Por favor, escreva o conteúdo do post");
      return;
    }
    setSaving(true);
    try {
      await authAxios.post("/social-agent/posts", newPost);
      toast.success("Post criado com sucesso!");
      onSuccess?.();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar post");
    }
    setSaving(false);
  };

  const handleSaveSchedule = async (scheduleData, onSuccess) => {
    setSaving(true);
    try {
      await authAxios.put("/social-agent/subscription/schedule", scheduleData);
      toast.success("Agendamento salvo!");
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const handleGenerateAIContent = async ({ aiStyle, restaurantInfo }) => {
    try {
      const res = await authAxios.post("/social-agent/posts/generate", {
        content_style: aiStyle,
        restaurant_info: restaurantInfo || "",
        platforms: ["facebook"],
        publish: false
      });
      toast.success("Conteúdo gerado com IA!");
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar conteúdo");
      return null;
    }
  };

  const handlePublishAIPost = async ({ aiStyle, restaurantInfo }, onSuccess) => {
    setSaving(true);
    try {
      await authAxios.post("/social-agent/posts/generate", {
        content_style: aiStyle,
        restaurant_info: restaurantInfo || "",
        platforms: ["facebook"],
        publish: true
      });
      toast.success("Post publicado com sucesso!");
      onSuccess?.();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao publicar");
    }
    setSaving(false);
  };

  const hasSubscription = subscription?.subscribed || subscription?.status === "trial";
  const canPost = hasSubscription && (subscription?.features?.auto_posts || subscription?.plan === "premium");
  const canAds = hasSubscription && (subscription?.features?.ads || subscription?.plan === "premium");

  return {
    loading,
    saving,
    subscription,
    plans,
    status,
    posts,
    schedule,
    setSchedule,
    hasSubscription,
    canPost,
    canAds,
    fetchData,
    handleConnect,
    handleDisconnect,
    handleSubscribe,
    handleActivateTrial,
    handleCreatePost,
    handleSaveSchedule,
    handleGenerateAIContent,
    handlePublishAIPost,
  };
}
